import { ALLOWED_TAGS } from '@shared/tags';
import { listSongs } from '@/audio/songs';

export type SchemaFieldType =
  | { type: 'number'; min?: number; max?: number; step?: number }
  | { type: 'string'; multiline?: boolean }
  | { type: 'color' }
  | { type: 'enum'; options: readonly string[]; labels?: readonly string[] }
  | { type: 'boolean' }
  | { type: 'tags' }
  | { type: 'tag-picker'; allowed: readonly string[] };

const SONG_OPTIONS = listSongs();

export interface SchemaField {
  label: string;
  path: readonly (string | number)[];
  field: SchemaFieldType;
}

export interface Schema {
  title: string;
  fields: readonly SchemaField[];
}

// ── Schemas ──────────────────────────────────────────────

export const metaSchema: Schema = {
  title: 'Level Metadata',
  fields: [
    { label: 'Name', path: ['name'], field: { type: 'string' } },
    { label: 'Author', path: ['author'], field: { type: 'string' } },
    { label: 'Description', path: ['description'], field: { type: 'string', multiline: true } },
    { label: 'Difficulty', path: ['difficulty'], field: { type: 'enum', options: ['easy', 'normal', 'hard', 'insane', 'triggered'] } },
    { label: 'Par Time (ms)', path: ['parTime'], field: { type: 'number', min: 1000, step: 500 } },
    { label: 'Optimal Inputs', path: ['optimalInputs'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Tags', path: ['tags'], field: { type: 'tag-picker', allowed: ALLOWED_TAGS } },
  ],
};

export const settingsSchema: Schema = {
  title: 'Level Settings',
  fields: [
    { label: 'Initial Speed', path: ['initialSpeed'], field: { type: 'number', min: 0.25, max: 3, step: 0.05 } },
    { label: 'Base Color', path: ['baseColor'], field: { type: 'color' } },
    { label: 'Accent Color', path: ['accentColor'], field: { type: 'color' } },
    { label: 'Background', path: ['backgroundColor'], field: { type: 'color' } },
    { label: 'Grid Intensity', path: ['gridIntensity'], field: { type: 'number', min: 0, max: 1, step: 0.05 } },
    { label: 'Song', path: ['music', 'song'], field: {
      type: 'enum',
      options: SONG_OPTIONS.map((s) => s.id),
      labels: SONG_OPTIONS.map((s) => s.name),
    } },
    { label: 'BPM', path: ['music', 'bpm'], field: { type: 'number', min: 60, max: 200, step: 1 } },
    { label: 'Key', path: ['music', 'key'], field: { type: 'string' } },
  ],
};

export const spikeSchema: Schema = {
  title: 'Spike',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Width', path: ['size', 'width'], field: { type: 'number', min: 4, step: 1 } },
    { label: 'Height', path: ['size', 'height'], field: { type: 'number', min: 4, step: 1 } },
  ],
};

export const wallSchema: Schema = {
  title: 'Wall',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Width', path: ['size', 'width'], field: { type: 'number', min: 4, step: 1 } },
    { label: 'Height', path: ['size', 'height'], field: { type: 'number', min: 4, step: 1 } },
  ],
};

export const jumppadSchema: Schema = {
  title: 'Jump Pad',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Width', path: ['size', 'width'], field: { type: 'number', min: 8, step: 1 } },
    { label: 'Height', path: ['size', 'height'], field: { type: 'number', min: 4, step: 1 } },
    { label: 'Pad Size', path: ['visual', 'padSize'], field: { type: 'enum', options: ['small', 'medium', 'large'] } },
    { label: 'Color (override)', path: ['visual', 'color'], field: { type: 'color' } },
  ],
};

export const orbSchema: Schema = {
  title: 'Orb',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Diameter', path: ['size', 'width'], field: { type: 'number', min: 8, step: 1 } },
    { label: 'Orb Size', path: ['visual', 'orbSize'], field: { type: 'enum', options: ['small', 'medium', 'large'] } },
    { label: 'Color (override)', path: ['visual', 'color'], field: { type: 'color' } },
  ],
};

export const starSchema: Schema = {
  title: 'Star',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Index (0–2)', path: ['index'], field: { type: 'number', min: 0, max: 2, step: 1 } },
  ],
};

export const terrainFunctionSchema: Schema = {
  title: 'Terrain (Function)',
  fields: [
    { label: 'Function', path: ['floor', 'function'], field: { type: 'enum', options: ['sin', 'cos', 'tan', 'constant'] } },
    { label: 'Amplitude', path: ['floor', 'amplitude'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Frequency', path: ['floor', 'frequency'], field: { type: 'number', min: 0, step: 0.001 } },
    { label: 'Offset', path: ['floor', 'offset'], field: { type: 'number', step: 1 } },
    { label: 'Phase', path: ['floor', 'phase'], field: { type: 'number', step: 0.1 } },
  ],
};

export const terrainCeilingSchema: Schema = {
  title: 'Ceiling',
  fields: [
    { label: 'Function', path: ['ceiling', 'function'], field: { type: 'enum', options: ['sin', 'cos', 'tan', 'constant'] } },
    { label: 'Amplitude', path: ['ceiling', 'amplitude'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Frequency', path: ['ceiling', 'frequency'], field: { type: 'number', min: 0, step: 0.001 } },
    { label: 'Offset', path: ['ceiling', 'offset'], field: { type: 'number', step: 1 } },
    { label: 'Phase', path: ['ceiling', 'phase'], field: { type: 'number', step: 0.1 } },
  ],
};

export const terrainFlatSchema: Schema = {
  title: 'Terrain (Flat)',
  fields: [
    { label: 'Offset', path: ['floor', 'offset'], field: { type: 'number', step: 1 } },
  ],
};

export const segmentSchema: Schema = {
  title: 'Segment',
  fields: [
    { label: 'Mode', path: ['mode'], field: { type: 'enum', options: ['real', 'complex'] } },
    { label: 'Length', path: ['length'], field: { type: 'number', min: 100, step: 50 } },
    { label: 'Speed', path: ['speed'], field: { type: 'number', min: 0.25, max: 3, step: 0.05 } },
  ],
};

export const movingSchema: Schema = {
  title: 'Moving',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Width', path: ['size', 'width'], field: { type: 'number', min: 4, step: 1 } },
    { label: 'Height', path: ['size', 'height'], field: { type: 'number', min: 4, step: 1 } },
    { label: 'Motion', path: ['behavior', 'motion'], field: { type: 'enum', options: ['sine', 'cosine', 'circular'] } },
    { label: 'Amplitude', path: ['behavior', 'amplitude'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Frequency (Hz)', path: ['behavior', 'frequency'], field: { type: 'number', min: 0, step: 0.05 } },
    { label: 'Phase', path: ['behavior', 'phase'], field: { type: 'number', step: 0.1 } },
  ],
};

export const fragmentSchema: Schema = {
  title: 'Fragment',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Index (0–4)', path: ['index'], field: { type: 'number', min: 0, max: 4, step: 1 } },
  ],
};

export const powerupSchema: Schema = {
  title: 'Power-up',
  fields: [
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Type', path: ['powerupType'], field: { type: 'enum',
      options: ['taylor_expansion', 'fourier_shield', 'euler_identity', 'lhopital_rescue', 'inverse_function', 'eigenmode'] } },
  ],
};

// ── Trigger schemas ──────────────────────────────────────

export const terrainTunnelSchema: Schema = {
  title: 'Terrain (Tunnel)',
  fields: [
    { label: 'Shape', path: ['tunnel', 'shape'], field: { type: 'enum', options: ['circle', 'rose'] } },
    { label: 'Radius', path: ['tunnel', 'radius'], field: { type: 'number', min: 20, max: 200, step: 2 } },
    { label: 'Shape Morph (0..1)', path: ['tunnel', 'parameters', 'shapeMorph'], field: { type: 'number', min: 0, max: 1, step: 0.05 } },
    { label: 'Rose Petals', path: ['tunnel', 'parameters', 'rosePetals'], field: { type: 'number', min: 2, max: 8, step: 1 } },
  ],
};

export const triggerBaseSchema: Schema = {
  title: 'Trigger',
  fields: [
    { label: 'Type', path: ['type'], field: { type: 'enum',
      options: ['text', 'color', 'speed', 'camera', 'music', 'gravity', 'player_mode', 'spawn', 'mode', 'fog'] } },
    { label: 'Position', path: ['position'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Duration (ms)', path: ['duration'], field: { type: 'number', min: 0, step: 50 } },
    { label: 'Easing', path: ['easing'], field: { type: 'enum',
      options: ['linear', 'sine-in-out', 'ease-in', 'ease-out'] } },
  ],
};

export const triggerParamSchemas: Record<string, Schema> = {
  text: {
    title: 'Text params',
    fields: [
      { label: 'Text', path: ['params', 'text'], field: { type: 'string' } },
      { label: 'Style', path: ['params', 'style'], field: { type: 'enum', options: ['floating', 'fixed'] } },
    ],
  },
  color: {
    title: 'Color params',
    fields: [
      { label: 'Base Color', path: ['params', 'baseColor'], field: { type: 'color' } },
      { label: 'Accent Color', path: ['params', 'accentColor'], field: { type: 'color' } },
      { label: 'Background', path: ['params', 'backgroundColor'], field: { type: 'color' } },
    ],
  },
  speed: {
    title: 'Speed params',
    fields: [
      { label: 'Speed Multiplier', path: ['params', 'speed'], field: { type: 'number', min: 0.25, max: 3, step: 0.05 } },
    ],
  },
  camera: {
    title: 'Camera params',
    fields: [
      { label: 'Action', path: ['params', 'action'], field: { type: 'enum', options: ['shake', 'zoom', 'rotate'] } },
      { label: 'Intensity', path: ['params', 'intensity'], field: { type: 'number', min: 0, step: 0.1 } },
      { label: 'Zoom', path: ['params', 'zoom'], field: { type: 'number', min: 0.5, max: 2, step: 0.1 } },
      { label: 'Rotation (rad)', path: ['params', 'rotation'], field: { type: 'number', step: 0.1 } },
    ],
  },
  music: {
    title: 'Music params',
    fields: [
      { label: 'Action', path: ['params', 'action'], field: { type: 'enum', options: ['add_layer', 'remove_layer', 'change_bpm'] } },
      { label: 'Layer', path: ['params', 'layer'], field: { type: 'string' } },
      { label: 'BPM', path: ['params', 'bpm'], field: { type: 'number', min: 60, max: 200, step: 1 } },
    ],
  },
  gravity: {
    title: 'Gravity params',
    fields: [
      { label: 'Direction', path: ['params', 'direction'], field: { type: 'enum', options: ['down', 'up'] } },
      { label: 'Strength', path: ['params', 'strength'], field: { type: 'number', min: 0, step: 0.1 } },
    ],
  },
  player_mode: {
    title: 'Player Mode params',
    fields: [
      { label: 'Mode', path: ['params', 'playerMode'], field: { type: 'enum',
        options: ['triangle', 'wave_rider', 'orbiter', 'tangent', 'phase_shifter'] } },
    ],
  },
  spawn: {
    title: 'Spawn params',
    fields: [
      { label: 'Note', path: ['params', 'note'], field: { type: 'string' } },
    ],
  },
  mode: {
    title: 'Mode params',
    fields: [
      { label: 'Target Mode', path: ['params', 'targetMode'], field: { type: 'enum', options: ['real', 'complex'] } },
      { label: 'Transition', path: ['params', 'transition'], field: { type: 'enum', options: ['portal', 'warp', 'fade'] } },
    ],
  },
  fog: {
    title: 'Fog params',
    fields: [
      { label: 'Near', path: ['params', 'near'], field: { type: 'number', min: 0, step: 5 } },
      { label: 'Far', path: ['params', 'far'], field: { type: 'number', min: 0, step: 10 } },
      { label: 'Color', path: ['params', 'color'], field: { type: 'color' } },
    ],
  },
};

export const decorationSchema: Schema = {
  title: 'Decoration',
  fields: [
    { label: 'Type', path: ['type'], field: { type: 'enum', options: ['particle_emitter', 'glow_line', 'text', 'shape'] } },
    { label: 'Along', path: ['position', 'along'], field: { type: 'number', min: 0, step: 1 } },
    { label: 'Across', path: ['position', 'across'], field: { type: 'number', step: 1 } },
    { label: 'Up', path: ['position', 'up'], field: { type: 'number', step: 1 } },
    { label: 'Properties (JSON)', path: ['properties'], field: { type: 'string', multiline: true } },
  ],
};
